import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '@/environments/environment';

const BASE_URL = `${API_BASE_URL}/api/v1`;
const SITE_BASE_URL = `${API_BASE_URL}/api/v1/site/products`;
const CUSTOMER_BASE_URL = `${API_BASE_URL}/api/v1/product-reviews`;

export type ReviewMediaType = 'Image' | 'Video';

export interface PublicReviewMediaDto {
  mediaUrl: string;
  mediaType: ReviewMediaType;
  contentType: string;
  sortOrder: number;
}

export interface PublicProductReviewDto {
  productReviewId: string;
  productId: string;
  variantId: string | null;
  rating: number;
  text: string | null;
  customerDisplayName: string;
  productNameSnapshot: string;
  variantDisplaySnapshot: string | null;
  createdAtUtc: string;
  media: PublicReviewMediaDto[];
}

export interface PublicProductReviewsResponse {
  items: PublicProductReviewDto[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type PublicReviewSort =
  | 'newest'
  | 'highest-rating'
  | 'lowest-rating'
  | 'media-first';

export interface CreateProductReviewMedia {
  mediaAssetId: string;
}

export interface UploadReviewMediaResponse {
  mediaAssetId: string;
  url: string;
}

export interface CreateProductReviewRequest {
  sellerOrderItemId: string;
  rating: number;
  text?: string;
  media: CreateProductReviewMedia[];
  customerDisplayName: string;
}

export interface ProductReviewResultDto {
  productReviewId: string;
  status: string;
  isPublic: boolean;
}

export interface CustomerProductReviewMediaDto {
  mediaUrl: string;
  mediaType: ReviewMediaType;
  contentType: string;
  sortOrder: number;
}

export interface CustomerProductReviewDto {
  productReviewId: string;
  sellerOrderItemId: string;
  productId: string;
  variantId: string | null;
  sellerId: string;
  rating: number;
  text: string | null;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Hidden';
  isPublic: boolean;
  isWithdrawnByCustomer: boolean;
  latestDecisionReason: string | null;
  productNameSnapshot: string;
  variantDisplaySnapshot: string | null;
  sellerStoreNameSnapshot: string;
  orderNumberSnapshot: string;
  sellerOrderNumberSnapshot: string;
  media: CustomerProductReviewMediaDto[];
  productImageUrl: string;
}

export interface CustomerProductReviewsResponse {
  items: CustomerProductReviewDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  productImageUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductReviewsApi {
  private readonly http = inject(HttpClient);

  getProductReviews(
    productId: string,
    page: number = 1,
    pageSize: number = 10,
    rating?: number,
    hasMedia?: boolean,
    sort: PublicReviewSort = 'newest',
  ): Observable<PublicProductReviewsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sort', sort);

    if (rating !== undefined && rating !== null) {
      params = params.set('rating', rating.toString());
    }
    if (hasMedia !== undefined && hasMedia !== null) {
      params = params.set('hasMedia', hasMedia.toString());
    }

    return this.http.get<PublicProductReviewsResponse>(
      `${SITE_BASE_URL}/${productId}/reviews`,
      { params },
    );
  }

  createReview(
    payload: CreateProductReviewRequest,
  ): Observable<ProductReviewResultDto> {
    return this.http.post<ProductReviewResultDto>(CUSTOMER_BASE_URL, payload);
  }

  uploadReviewMedia(file: File): Observable<UploadReviewMediaResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', '3');

    return this.http.post<UploadReviewMediaResponse>(
      `${API_BASE_URL}/api/v1/media/upload`,
      formData,
    );
  }

  listMyReviews(
    page: number = 1,
    pageSize: number = 20,
  ): Observable<CustomerProductReviewsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<CustomerProductReviewsResponse>(CUSTOMER_BASE_URL, {
      params,
    });
  }
}
